import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideAnimations, BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
  TranslateStore,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  HttpClient,
  HttpClientModule,
  provideHttpClient,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { importProvidersFrom } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { AppComponent } from './app/app.component';

import { SignInComponent } from './app/features/sign-in/sign-in.page';
import { DashboardComponent } from './app/features/dashboard/dashboard.component';
import { LayoutComponent } from './app/features/layout/layout.component';
import { CallsComponent } from './app/features/calls/calls.component';
import { DisputesComponent } from './app/features/disputes/disputes.component';
import { NotificationsComponent } from './app/features/notifications/notifications.component';
import { PaymentsComponent } from './app/features/payments/payments.component';
import { UsersComponent } from './app/features/users/users.component';
import { UserDetailsComponent } from './app/features/user-details/user-details.component';
import { CallDetailsComponent } from './app/features/call-details/call-details.component';
import HttpConfigInterceptor from './app/core/interceptors/http-config.interceptor';
import authGuard, { otpGuard } from './app/core/guards/auth.guard';
import { ServiceDetailsComponent } from './app/features/service-details/service-details.component';
import { OtpVerificationComponent } from './app/features/otp-verification/otp-verification.component';
import { NotificationHistoryComponent } from './app/features/notification-history/notification-history.component';

// Translation loader function
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const routes = [
  { path: 'signIn', component: SignInComponent },
  {
    path: 'otpVerification',
    component: OtpVerificationComponent,
    canActivate: [otpGuard],
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard],
    children: [
      { path: 'dashboard', component: DashboardComponent },
      { path: 'user', component: UsersComponent },
      { path: 'user-details/:id/:type', component: UserDetailsComponent },
      { path: 'call', component: CallsComponent },
      { path: 'dispute', component: DisputesComponent },
      { path: 'payment', component: PaymentsComponent },
      { path: 'notification', component: NotificationsComponent },
      { path: 'call-details/:callId', component: CallDetailsComponent },
      { path: 'call-details/:callId/:disputeId', component: CallDetailsComponent },
      { path: 'dispute/:disputeId', component: ServiceDetailsComponent },
      { path: 'notification/notifications-history', component: NotificationHistoryComponent },
    ],
  },
  { path: '**', redirectTo: 'signIn' },
];

// Bootstrap the application
bootstrapApplication(AppComponent, {
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpConfigInterceptor,
      multi: true,
    },
    provideRouter(routes),
    provideAnimations(),
    MessageService,
    provideHttpClient(),
    importProvidersFrom(
      BrowserModule,
      ToastModule,
      InputTextModule,
      PasswordModule,
      BrowserAnimationsModule,
      ButtonModule,
      PickerModule,
      HttpClientModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    TranslateService,
    TranslateStore,
    { provide: LocationStrategy, useClass: HashLocationStrategy },
  ],
}).catch(() => {
  // Do Something
});

window.addEventListener('unhandledrejection', () => {});
